import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import * as React from 'react';
import { createElement } from 'react';
import { getEmptyImage } from 'react-dnd-html5-backend';
import styled from 'styled-components';
import { spacing1x } from '../../../../styles/spacing.js';
import { getColorWithOpacity } from '../../../../utils/color.js';
import { Button } from '../../../Button/Button.js';
import { Checkbox } from '../../../Checkbox/Checkbox.js';
import { IconArrowDown } from '../../../Icons/IconArrowDown.js';
import { IconArrowRight } from '../../../Icons/IconArrowRight.js';
import { IconDrag } from '../../../Icons/IconDrag.js';
import { Tooltip } from '../../../Tooltip/Tooltip.js';
import { TREE_LINE_HEIGHT, TREE_HORIZONTAL_SEPARATION, TREE_DRAG_ANIM_TIME, TREE_DRAG_ANIM_CURVE, TREE_DEFAULT_BORDER_WIDTH } from '../../constants.js';
import { useTreeNodeDragDrop } from '../../hooks/use_drag_drop.js';
import { dnd } from '../../Tree.js';
import { TreeDragHandle } from '../TreeDragHandle.js';
import { TreeLabel } from '../TreeLabel.js';
import { TreeNodeExpandIconWrapper } from './ExpandIconWrapper.js';
import { TreeNodeHorizontalDashes } from './HorizontalDashes.js';
import { TreeNodeDropRegion } from './NodeDropRegion.js';
import { TreeDropBar } from './TreeDropBar.js';
import { TreeNodeVerticalDashes } from './VerticalDashes.js';
import { jsxs, jsx } from 'react/jsx-runtime';

var StyledContainer = styled.div.withConfig({
  displayName: "TreeNode__StyledContainer",
  componentId: "sc-1ruxqz3-0"
})(["&&{display:flex;}"]);
var StyledHorizontalDashesWrapper = styled.div.withConfig({
  displayName: "TreeNode__StyledHorizontalDashesWrapper",
  componentId: "sc-1ruxqz3-1"
})(["&&{position:relative;width:", "px;flex:0 0 auto;}"], function (_ref) {
  var width = _ref.width;
  return width;
});
var StyledHoverCapture = styled.div.withConfig({
  displayName: "TreeNode__StyledHoverCapture",
  componentId: "sc-1ruxqz3-2"
})(["&&{flex:1;}"]);
var StyledIconWrapper = styled.div.withConfig({
  displayName: "TreeNode__StyledIconWrapper",
  componentId: "sc-1ruxqz3-3"
})(function () {
  return {
    lineHeight: TREE_LINE_HEIGHT,
    marginInlineStart: TREE_HORIZONTAL_SEPARATION,
    zIndex: 4
  };
});
var StyledTreeNode = styled.div.withConfig({
  displayName: "TreeNode__StyledTreeNode",
  componentId: "sc-1ruxqz3-4"
})(function (_ref2) {
  var emphasized = _ref2.emphasized,
    isDragging = _ref2.isDragging,
    isDragOver = _ref2.isDragOver,
    selected = _ref2.selected,
    theme = _ref2.theme;
  return _objectSpread({
    borderRadius: spacing1x,
    display: "flex",
    position: "relative",
    padding: "2px",
    transition: "all ".concat(TREE_DRAG_ANIM_TIME, " ").concat(TREE_DRAG_ANIM_CURVE)
  }, function () {
    if (isDragging) {
      return {
        backgroundColor: theme.neutralColors.pearl
      };
    } else if (isDragOver) {
      return {
        backgroundColor: getColorWithOpacity(theme.CTAColors.primary, 0.1),
        outline: "dashed ".concat(TREE_DEFAULT_BORDER_WIDTH, "px ").concat(theme.CTAColors.primary)
      };
    } else if (selected) {
      return {
        backgroundColor: theme.neutralColors.silver
      };
    } else if (emphasized) {
      return {
        backgroundColor: theme.neutralColors.pearl
      };
    }
  }());
});
var TreeNode = function TreeNode(_ref3) {
  var _ref3$canDrag = _ref3.canDrag,
    canDrag = _ref3$canDrag === void 0 ? function () {
      return true;
    } : _ref3$canDrag,
    _ref3$canDrop = _ref3.canDrop,
    canDrop = _ref3$canDrop === void 0 ? function () {
      return true;
    } : _ref3$canDrop,
    canOrder = _ref3.canOrder,
    context = _ref3.context,
    focused = _ref3.focused,
    initialHeight = _ref3.height,
    node = _ref3.node,
    onChange = _ref3.onChange,
    onCollapse = _ref3.onCollapse,
    onDragStart = _ref3.onDragStart,
    onExpand = _ref3.onExpand,
    renderButtons = _ref3.renderButtons,
    renderIcon = _ref3.renderIcon,
    renderLabel = _ref3.renderLabel,
    variant = _ref3.variant;
  var ref = React.useRef(null);
  var _React$useState = React.useState(initialHeight || 0),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    height = _React$useState2[0],
    setHeight = _React$useState2[1];
  var _React$useState3 = React.useState(false),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    hovered = _React$useState4[0],
    setHovered = _React$useState4[1];
  React.useEffect(function () {
    var _ref$current;
    setHeight(((_ref$current = ref.current) === null || _ref$current === void 0 ? void 0 : _ref$current.clientHeight) || initialHeight || 0);
  }, [initialHeight]);
  var width = 32;
  var emphasized = focused || hovered;
  var selected = React.useMemo(function () {
    if ((variant === null || variant === void 0 ? void 0 : variant.type) === "select") {
      return variant.isSelected({
        context: context,
        node: node
      });
    } else {
      return false;
    }
  }, [context, node, variant]);
  var _useTreeNodeDragDrop = useTreeNodeDragDrop({
      canDrag: canDrag,
      canDrop: canDrop,
      canOrder: canOrder,
      context: context,
      node: node,
      onChange: onChange,
      onCollapse: onCollapse,
      onDragStart: onDragStart
    }),
    canDragNode = _useTreeNodeDragDrop.canDragNode,
    connectDragPreview = _useTreeNodeDragDrop.connectDragPreview,
    connectDragSource = _useTreeNodeDragDrop.connectDragSource,
    connectDropBottomBarTarget = _useTreeNodeDragDrop.connectDropBottomBarTarget,
    connectDropTarget = _useTreeNodeDragDrop.connectDropTarget,
    connectDropTopBarTarget = _useTreeNodeDragDrop.connectDropTopBarTarget,
    isBottomDropBarVisible = _useTreeNodeDragDrop.isBottomDropBarVisible,
    isDragging = _useTreeNodeDragDrop.isDragging,
    isDragOverBottomBar = _useTreeNodeDragDrop.isDragOverBottomBar,
    isDragOverNode = _useTreeNodeDragDrop.isDragOverNode,
    isDragOverTopBar = _useTreeNodeDragDrop.isDragOverTopBar,
    isMouseDragging = _useTreeNodeDragDrop.isMouseDragging,
    isTopDropBarVisible = _useTreeNodeDragDrop.isTopDropBarVisible;
  var _dnd$useDragLayer = dnd.useDragLayer(function (monitor) {
      return {
        hasActiveDrag: monitor.isDragging()
      };
    }),
    hasActiveDrag = _dnd$useDragLayer.hasActiveDrag;
  React.useEffect(function () {
    connectDragPreview(getEmptyImage(), {
      captureDraggingState: true
    });
  }, [connectDragPreview]);
  React.useEffect(function () {
    if (isMouseDragging && isDragOverNode && context.isCollapsed()) {
      onExpand();
    }
  }, [context, isMouseDragging, isDragOverNode, onExpand]);
  return /*#__PURE__*/jsxs(StyledContainer, {
    ref: ref,
    children: [/*#__PURE__*/jsx(TreeNodeVerticalDashes, {
      hasDescenderByLevel: context.hasDescenderByLevel,
      isLastChild: context.isLastChild(),
      width: width,
      height: height,
      canAnimate: hasActiveDrag,
      isDragOverTopBar: isDragOverTopBar,
      isDragOverBottomBar: isDragOverBottomBar
    }), /*#__PURE__*/jsx(StyledHorizontalDashesWrapper, {
      width: width,
      children: /*#__PURE__*/jsx(TreeNodeHorizontalDashes, {
        isDragOverTopBar: isDragOverTopBar,
        isDragOverBottomBar: isDragOverBottomBar
      })
    }), /*#__PURE__*/jsxs(StyledHoverCapture, {
      onMouseEnter: function onMouseEnter() {
        return !hasActiveDrag && setHovered(true);
      },
      onMouseLeave: function onMouseLeave() {
        return !hasActiveDrag && setHovered(false);
      },
      children: [isTopDropBarVisible && /*#__PURE__*/jsx(TreeDropBar, {
        position: "top",
        isDragOver: isDragOverTopBar,
        dndRef: connectDropTopBarTarget
      }), /*#__PURE__*/jsxs(StyledTreeNode, {
        emphasized: emphasized && !isDragOverBottomBar && !isDragOverTopBar,
        isDragging: isDragging,
        isDragOver: isDragOverNode,
        selected: (variant === null || variant === void 0 ? void 0 : variant.type) === "select" ? selected : void 0,
        onClick: (variant === null || variant === void 0 ? void 0 : variant.type) === "select" ? function () {
          return variant.onSelect({
            context: context,
            node: node
          });
        } : void 0,
        children: [isDragging && /*#__PURE__*/jsx(TreeNodeDropRegion, {
          ref: connectDropTarget
        }), /*#__PURE__*/jsx(TreeNodeExpandIconWrapper, {
          children: context.hasChildren() && height > 0 && (context.isCollapsed() ? /*#__PURE__*/jsx(IconArrowRight, {
            onClick: onExpand,
            size: 16
          }) : /*#__PURE__*/jsx(IconArrowDown, {
            onClick: onCollapse,
            size: 16
          }))
        }), onChange && hovered && /*#__PURE__*/jsx(TreeDragHandle, {
          disabled: !canDragNode,
          isDragPlaceholder: isDragging,
          ref: connectDragSource,
          children: /*#__PURE__*/jsx(IconDrag, {
            size: 16
          })
        }), function () {
          if (!isDragging && (variant === null || variant === void 0 ? void 0 : variant.type) === "multiselect") {
            var checkboxProps = variant.renderCheckbox({
              context: context,
              node: node
            });
            return /*#__PURE__*/jsx(StyledIconWrapper, {
              children: /*#__PURE__*/jsx(Checkbox, _objectSpread(_objectSpread({}, checkboxProps), {}, {
                "aria-label": "Select item",
                onFocus: function onFocus(event) {
                  return event.stopPropagation();
                },
                size: 16,
                tabIndex: focused && !checkboxProps.disabled ? 0 : -1
              }))
            });
          }
        }(), function () {
          if (!isDragging && renderIcon) {
            var el = renderIcon({
              context: context,
              node: node
            });
            if (el) {
              return /*#__PURE__*/jsx(StyledIconWrapper, {
                children: /*#__PURE__*/React.cloneElement(el, {
                  size: 16
                })
              });
            }
          }
        }(), /*#__PURE__*/jsx(TreeLabel, {
          isDragging: isDragging,
          children: renderLabel({
            context: context,
            node: node
          })
        }), !isDragging && renderButtons && (hovered || selected) && renderButtons({
          context: context,
          node: node
        }).map(function (_ref4, i) {
          var buttonProps = _ref4.buttonProps,
            tooltipProps = _ref4.tooltipProps;
          var buttonEl = /*#__PURE__*/createElement(Button, _objectSpread(_objectSpread({}, buttonProps), {}, {
            hideLabel: true,
            key: i,
            onFocus: function onFocus(event) {
              return event.stopPropagation();
            },
            onKeyDown: function onKeyDown(event) {
              return event.stopPropagation();
            },
            variant: "ghost"
          }));
          if (!tooltipProps) {
            return buttonEl;
          } else {
            return /*#__PURE__*/createElement(Tooltip, _objectSpread(_objectSpread({}, tooltipProps), {}, {
              key: i
            }), buttonEl);
          }
        })]
      }), isBottomDropBarVisible && /*#__PURE__*/jsx(TreeDropBar, {
        position: "bottom",
        isDragOver: isDragOverBottomBar,
        dndRef: connectDropBottomBarTarget
      })]
    })]
  });
};
TreeNode.displayName = "TreeNode";

export { StyledTreeNode, TreeNode };
